import React, { createContext, useContext, useState, useEffect } from 'react';
import { UpgradeModal } from '../components/subscription';
import { registerUpgradeModalCallback } from '../utils/apiInterceptor';

const SubscriptionContext = createContext(null);

/**
 * SubscriptionProvider - Global subscription state and upgrade modal management
 *
 * Wraps the app to provide:
 * - Global upgrade modal that can be triggered from anywhere
 * - Automatic 402 response handling via API interceptor
 * - Centralized subscription UI state
 */
export function SubscriptionProvider({ children }) {
  const [upgradeModalState, setUpgradeModalState] = useState({
    isOpen: false,
    feature: null,
  });

  // Register callback with API interceptor on mount
  useEffect(() => {
    registerUpgradeModalCallback((feature) => {
      setUpgradeModalState({
        isOpen: true,
        feature,
      });
    });
  }, []);

  const openUpgradeModal = (feature = null) => {
    setUpgradeModalState({
      isOpen: true,
      feature,
    });
  };

  const closeUpgradeModal = () => {
    setUpgradeModalState({
      isOpen: false,
      feature: null,
    });
  };

  return (
    <SubscriptionContext.Provider
      value={{
        openUpgradeModal,
        closeUpgradeModal,
      }}
    >
      {children}

      {/* Global upgrade modal */}
      <UpgradeModal
        isOpen={upgradeModalState.isOpen}
        onClose={closeUpgradeModal}
        feature={upgradeModalState.feature}
      />
    </SubscriptionContext.Provider>
  );
}

/**
 * Hook to access subscription context
 */
export function useSubscriptionContext() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptionContext must be used within SubscriptionProvider');
  }
  return context;
}
